import { useLayoutEffect, useMemo, useState, useRef, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import DecisionModal from '@/components/custom/InfoPopups/DecisionModal';
import ErrorPopup from '@/components/custom/InfoPopups/ErrorPopup';
import SuccessPopup from "@/components/custom/InfoPopups/SuccessPopup";
import LoaderWithBackdrop from '@/components/custom/LoaderWithBackdrop';
import { Button } from '@/components/ui/button';
import { EditQuestionModal } from "@/features/question/components/organisms/EditQuestionModal";
import { Question } from '@/lib/supabase/types';
// import { useProjectStore } from '@/lib/zustand/useStore';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { Menubar, MenubarCheckboxItem, MenubarContent, MenubarItem, MenubarMenu, MenubarTrigger } from '@/components/ui/menubar';
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from '@/components/ui/separator';
import { QuestionFormSchemaType } from '@/features/question/components/organisms/QuestionForm';
// import { ReplySchemaType } from '@/features/question/components/organisms/ReplyModal';
import { useArchiveResponse } from '@/features/response/hooks/useMutation/useArchiveResponse';
import { useChangeValidStatus } from '@/features/response/hooks/useMutation/useChangeValidStatus';
import { UserAvatar } from '@/features/user/components/atoms/user-avatar';
import { Tables } from '@/lib/supabase/supabase_types_override';
import { sortByCreatedAt } from "@/lib/utils/sortByCreatedAt";
import { Archive, ArchiveRestore, Pencil, Settings, Trash2 } from 'lucide-react';

// import { ReplyBox } from '@/features/question/components/organisms/ReplyBox';
import { useDeleteQuestion } from '@/features/question/hooks/useMutation/useDeleteQuestion';
import { useUpdateQuestion } from '@/features/question/hooks/useMutation/useUpdateQuestion';
import { useGetQuestionsBySurveyId } from '@/features/question/hooks/useQuery/useGetQuestionsBySurveyId';
import { CheckProjectAccess } from '@/features/rbac/components/molecules/CheckProjectAccess';
import { Response } from "@/features/response/components/organisms/Response";
// import { useAddResponse } from '@/features/response/hooks/useMutation/useAddResponse';
import { UserWithRoles } from '@/lib/utils/query/rbac/resolveRoles';
import { ResponseSection } from '@/features/question/components/organisms/ResponseSection';


export function Questions() {
    // TODO: Refactor - get rid of refetch, addResponse - invalidate queries instead

    const { surveyId } = useParams();
    const { data: questions, isLoading, refetch } = useGetQuestionsBySurveyId({
        surveyId: surveyId,
    });
    const [searchParams] = useSearchParams();
    const selectedQuestionId = searchParams.get('questionId') || null;
    const [showQuestionModal, setShowQuestionModal] = useState(false);

    const [showArchivedResponses, setShowArchivedResponses] = useState<boolean>(false);
    // const [showReplyModal, setShowReplyModal] = useState<boolean>(false);

    const [selectedQuestion, setSelectedQuestion] = useState(questions?.questions?.find((q) => q.id === selectedQuestionId));
    const { mutateAsync: archiveResponse, isPending: isArchivingResponse } = useArchiveResponse();

    // TODO: Move replyHandler to ResonseSection component?
    // const { mutateAsync: addResponse, isPending: isPendingAddResponse } = useAddResponse(surveyId);
    // const replyHandler = (question_id: string) => async (formData: ReplySchemaType) => {

    //     await addResponse({ questions: questions?.questions, questionId: question_id, response: formData });

    //     setShowReplyModal(false);
    //     refetch();
    // }
    const { mutateAsync: changeValidStatus } = useChangeValidStatus();

    const onRemoveOrRetrieveResponseHandler = async (response: Tables<'response'>, archived: boolean) => {
        if (archived) {
            const userDecision = await DecisionModal({ message: 'Response will be retrieved', confirmButtonText: "Retrieve" });
            if (!userDecision.isConfirmed) return;

            await archiveResponse({
                responseId: response.id,
                archived: false
            });
            refetch();
        } else {
            const userDecision = await DecisionModal({ message: 'You will be able to recover this response later.' });
            if (!userDecision.isConfirmed) return;
            await archiveResponse({
                responseId: response.id,
                archived: true
            });
            await changeValidStatus({
                response_id: response.id,
                is_valid: false,
                selected_user_id: response?.user_id
            });
            refetch();
        }
    }

    useLayoutEffect(() => {
        if (selectedQuestionId) {
            setSelectedQuestion(questions?.questions?.find((q) => q.id === selectedQuestionId));
        }
    }, [selectedQuestionId, questions]);


    // * Handle Archive, Restore and Delete Question
    const { mutateAsync: updateQuestion, isPending: isPendingUpdateQuestion } = useUpdateQuestion();
    const { mutateAsync: deleteQuestion, isPending: isPendingDeleteQuestion } = useDeleteQuestion();

    const archiveQuestionHandler = async (question_id: string) => {
        const userDecision = await DecisionModal({ title: "Archive this question?", confirmButtonText: "Archive", message: 'You will be able to restore this question later' });
        if (!userDecision.isConfirmed) return;

        try {
            await updateQuestion({ data: { id: question_id, archived: true } });
            SuccessPopup("Question archived successfully");
        } catch (error) {
            ErrorPopup("Error archiving question");
        }
    };

    const restoreQuestionHandler = async (question_id: string) => {
        const userDecision = await DecisionModal({ title: "Restore", confirmButtonText: "Restore", message: 'Do you really want to restore this question?' });
        if (!userDecision.isConfirmed) return;

        try {
            await updateQuestion({ data: { id: question_id, archived: false } });
            SuccessPopup("Question restored successfully");
        } catch (error) {
            ErrorPopup("Error restoring question");
        }
    };

    const deleteQuestionHandler = async (question_id: string) => {
        const userDecision = await DecisionModal({ title: "Delete", confirmButtonText: "Delete", message: 'Do you really want to delete this question? Once deleted, you will not be able to recover this question' });
        if (!userDecision.isConfirmed) return;

        try {
            await deleteQuestion(question_id);
            SuccessPopup("Question deleted successfully");
        } catch (error) {
            ErrorPopup("Error deleting question");
        }
    };

    const checkIfQuestionsHaveResponse = (questionData: Partial<Question> | undefined) => {
        if (questionData?.response && Array.isArray(questionData.response) && questionData.response.length > 0) {
            return true;
        }
        return false
    }

    const { mutateAsync: editQuestion } = useUpdateQuestion()
    const onEditQuestionSubmitHandler = async (values: QuestionFormSchemaType) => {
        await editQuestion({
            data: values
        }).then(() => {
            SuccessPopup("Question updated successfully");
            setShowQuestionModal(false);
        }).catch((error) => {

            if (error instanceof Error) {
                ErrorPopup(error.message || "Error updating question");
            }
            else {
                ErrorPopup("Error updating question");
            }
        })
    }
    const responseId = searchParams.get('responseId') || null;

    const responses = useMemo(() => {
        if (!selectedQuestion?.response) return null;
        return selectedQuestion.response.filter(r => (r.archived && showArchivedResponses) || !r.archived).sort(sortByCreatedAt);
    }, [selectedQuestion, showArchivedResponses]);

    // Scroll to the bottom automatically
    const bottomRef = useRef<HTMLDivElement | null>(null)
    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" })
    }, [responses])

    if (isLoading) return <LoaderWithBackdrop />

    return (
        <>
            <div className="flex flex-col w-full gap-4 overflow-hidden grow" style={{ maxHeight: 'calc(100svh - 64px - 16px)' }}>
                {(isPendingUpdateQuestion || isPendingDeleteQuestion || isArchivingResponse) && (<LoaderWithBackdrop />)}

                <EditQuestionModal
                    show={showQuestionModal}
                    onHide={() => setShowQuestionModal(false)}
                    questionData={selectedQuestion}
                    isQuestionPropertiesDisabled={checkIfQuestionsHaveResponse(selectedQuestion)}
                    onSubmit={onEditQuestionSubmitHandler}
                />

                <div className="flex flex-col w-full gap-4 overflow-hidden grow" >
                    <div className="flex flex-col flex-1 min-h-0 gap-4">
                        <div className="flex flex-col flex-1 min-h-0 gap-2">
                            {selectedQuestion && (
                                <div className="flex justify-between">
                                    <div className="flex justify-between gap-2 pb-3 m-0 border-b grow">
                                        <div className="flex flex-col w-4/5 justify-center">
                                            <h1 className="text-sm font-semibold">{selectedQuestion && selectedQuestion.question}</h1>
                                            <p className="text-xs">{selectedQuestion && selectedQuestion.description}</p>

                                            { selectedQuestion?.selected_configuration?.conditionals?.enabled && (<CheckProjectAccess>
                                                <Separator className="my-2" />

                                                <div className="w-full text-xs">
                                                    <p className="font-bold">Users that met condition: </p>
                                                    {selectedQuestion?.condition_met_for_users?.length || 0 > 0 ? (
                                                        <Collapsible className='flex gap-2'>
                                                            <div className="flex gap-2">
                                                                {selectedQuestion?.condition_met_for_users?.slice(0, 2).map((user, i) => (
                                                                    <UserCard key={user.id} user={user} i={i} length={selectedQuestion?.condition_met_for_users?.length || 0} />
                                                                ))}
                                                            </div>
                                                            {(selectedQuestion?.condition_met_for_users?.length || 0) > 2 && (
                                                                <CollapsibleTrigger className="flex flex-col items-start gap-1">
                                                                    <Button variant="link" className="p-0 h-min">
                                                                        Show more...
                                                                    </Button>
                                                                </CollapsibleTrigger>
                                                            )}
                                                            <CollapsibleContent>
                                                                {selectedQuestion?.condition_met_for_users?.slice(0).map((user, i) => (
                                                                    <UserCard key={user.id} user={user} i={i} length={selectedQuestion?.condition_met_for_users?.length || 0} />
                                                                ))}
                                                            </CollapsibleContent>
                                                        </Collapsible>
                                                    ) : (
                                                        <p>No users met the condition</p>
                                                    )}
                                                </div>
                                            </CheckProjectAccess>)}
                                        </div>

                                        {selectedQuestion?.id && (
                                            <Menubar className="border-0 p-0">
                                                <MenubarMenu >

                                                    <MenubarTrigger asChild>
                                                        <Button variant="outline" size="sm" className="rounded-md text-xs gap-1">
                                                            <Settings size={16} /> Options
                                                        </Button>
                                                    </MenubarTrigger>

                                                    <MenubarContent align='end'>
                                                        <MenubarCheckboxItem
                                                            checked={showArchivedResponses}
                                                            onCheckedChange={(checked) => {
                                                                setShowArchivedResponses(checked);
                                                            }}
                                                            className='text-xs'
                                                        >
                                                            Show archived responses
                                                        </MenubarCheckboxItem>
                                                        <CheckProjectAccess question_update>
                                                            <MenubarItem className='flex gap-2 text-xs' onClick={() => setShowQuestionModal(true)}>
                                                                <Pencil size={16} /> Edit question
                                                            </MenubarItem>
                                                            {selectedQuestion?.archived ? (
                                                                <MenubarItem onClick={() => restoreQuestionHandler(selectedQuestion?.id)} asChild>
                                                                    <Button variant="accent" className="justify-start w-full text-xs" size='sm'>
                                                                        <ArchiveRestore size={16} /> Restore question
                                                                    </Button>
                                                                </MenubarItem>
                                                            ) : (
                                                                <MenubarItem onClick={() => archiveQuestionHandler(selectedQuestion?.id)} asChild>
                                                                    <Button variant="accent" className="justify-start w-full text-xs" size='sm'>
                                                                        <Archive size={16} /> Archive question
                                                                    </Button>
                                                                </MenubarItem>
                                                            )}
                                                        </CheckProjectAccess>
                                                        <CheckProjectAccess question_delete>
                                                            <MenubarItem onClick={() => deleteQuestionHandler(selectedQuestion?.id)} asChild>
                                                                <Button variant="destructive" className="justify-start w-full mt-1 text-xs" size='sm'>
                                                                    <Trash2 size={16} /> Delete question
                                                                </Button>
                                                            </MenubarItem>
                                                        </CheckProjectAccess>
                                                    </MenubarContent>
                                                </MenubarMenu>
                                            </Menubar>
                                        )}
                                    </div>
                                </div>
                            )}

                            {(() => {
                                if (questions?.questions?.length === 0) {
                                    return (
                                        <div className="grid h-full place-items-center">
                                            <p>No questions found</p>
                                        </div>
                                    )
                                }
                                if (questions?.questions?.length && !selectedQuestion) {
                                    return (
                                        <div className="flex flex-col items-center justify-center w-full h-full p-4 text-sm text-neutral-500">
                                            <p className="text-center">Select a question first to view its details.</p>
                                            <p className="text-center">You can choose a question from the sidebar menu.</p>
                                        </div>
                                    )
                                }
                                return (
                                    <ScrollArea className="flex-1 min-h-0 pe-3" type="always">

                                        <div className="flex flex-col h-full gap-3 p-0 m-0 rounded">
                                            {
                                                responses?.length ? responses.map(
                                                    (response, i) => (
                                                        <Response
                                                            key={response.id}
                                                            response={response}
                                                            i={i}
                                                            onRemoveOrRetrieveResponseHandler={onRemoveOrRetrieveResponseHandler}
                                                            responseId={responseId}
                                                            question={selectedQuestion}
                                                        />
                                                    )
                                                ) : (
                                                    <div className="flex flex-col items-center justify-center w-full h-full p-4 text-sm text-neutral-500">
                                                        <p>No response was provided for this question</p>
                                                        <p>To provide an answer, click the <span className="font-semibold">Add Response</span> button below</p>
                                                    </div>
                                                )
                                            }
                                        </div>

                                        {/* Scroll target for auto scroll to the bottom */}
                                        <div ref={bottomRef} />
                                    </ScrollArea>
                                )
                            })()}
                            
                            <ResponseSection 
                                questionData={selectedQuestion}
                                questions={questions?.questions || []}
                            />
                            
                        </div>
                    </div>

                </div>
            </div>
        </>
    )
}

// interface UserCardProps {
//     user: UserWithRoles,
//     i: number;
//     length: number;
// }
// function UserCard({ user, length, i }: UserCardProps) {
//     return (
//         <HoverCard>
//             <HoverCardTrigger>
//                 {user.first_name} {user.last_name}{(length || 0) > i + 1 && ', '}
//             </HoverCardTrigger>
//             <HoverCardContent className="w-96">
//                 <div className="flex items-center gap-2">
//                     <div className="">
//                         <UserAvatar user={user} className='text-white bg-primary' />
//                     </div>
//                     <div className="flex-col">
//                         <p className='font-bold'>{user.first_name} {user.last_name}</p>
//                         <p className='text-xs'>{user.email}</p>
//                         <p className='text-xs'>Role: <span className=''>{user.orgRole?.label || user.projectRole?.label || "-"}</span></p>
//                     </div>
//                 </div>
//             </HoverCardContent>
//         </HoverCard>
//     )
// }